import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import ResumeEditor, {
  suggestionToHighlightKey,
  applySuggestionToStructured,
  applyChatUpdates,
} from '../components/ResumeEditor';
import ResumePreview from '../components/ResumePreview';
import { emptyStructuredContent, extractTextFromFile, newCertificationId, newCustomSectionId, newEducationId, newExperienceId, newLanguageId, newProjectId } from '../utils/resumeExtract';
import { importChoiceKey, mergeImportedResume, selectionFromImport } from '../utils/resumeImport';
import IconButton from '../components/IconButton';
import Modal from '../components/Modal';
import {
  FiSave,
  FiZap,
  FiTarget,
  FiEdit3,
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiMessageSquare,
  FiSend,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiUpload,
  FiExternalLink,
  FiBriefcase,
  FiClipboard,
} from 'react-icons/fi';
import { queryKeys } from '../config/queryKeys';
import { normalizePagedList } from '../utils/listPagination';
import './ResumeStudio.css';

const CHAT_GREETING = 'Describe what the resume should say. Choices show on the field, and the one you pick is written into the form.';
const CHAT_SESSION_PREFIX = 'godash.resumeStudio.chat.';

function readChatSession(resumeId) {
  if (!resumeId) return null;
  try {
    const data = JSON.parse(localStorage.getItem(`${CHAT_SESSION_PREFIX}${resumeId}`) || 'null');
    if (!data || !Array.isArray(data.messages)) return null;
    const messages = data.messages
      .filter((item) => item && (item.role === 'user' || item.role === 'assistant') && item.text)
      .slice(-40);
    const proposals = Array.isArray(data.proposals) ? data.proposals.slice(0, 12) : [];
    return messages.length ? { messages, proposals } : null;
  } catch {
    return null;
  }
}

const SCORE_SECTIONS = [
  ['summary', 'Summary'],
  ['experience', 'Experience'],
  ['education', 'Education'],
  ['skills', 'Skills'],
  ['certifications', 'Certifications'],
  ['languages', 'Languages'],
  ['projects', 'Projects'],
];

function resumeHasSections(structured) {
  const s = structured || {};
  return Boolean(
    s.summary?.trim() ||
    s.experience?.length ||
    s.education?.length ||
    s.skills?.length ||
    s.certifications?.length ||
    s.languages?.length ||
    s.projects?.length ||
    s.custom_sections?.some((section) => section.body?.trim() || section.label?.trim())
  );
}

function scoreTone(score) {
  if (score == null || score === '') return 'empty';
  if (score >= 75) return 'good';
  if (score >= 50) return 'mid';
  return 'low';
}

const PREVIEW_WIDTH_KEY = 'godash.resumeStudio.previewWidth';
const PREVIEW_MIN = 260;
const EDITOR_MIN = 320;
const PREVIEW_NUDGE = 24;

function analysisTimestamp(analysis) {
  return Date.parse(analysis?.analyzed_at || analysis?.scored_at || '') || 0;
}

function newerAnalysis(incoming, current) {
  if (!incoming) return current || null;
  if (!current) return incoming;
  const incomingAt = analysisTimestamp(incoming);
  const currentAt = analysisTimestamp(current);
  if (currentAt && incomingAt && incomingAt < currentAt) return current;
  if (currentAt && !incomingAt) return current;
  return incoming;
}

function readStoredPreviewWidth() {
  try {
    const value = Number(window.localStorage.getItem(PREVIEW_WIDTH_KEY));
    if (!Number.isFinite(value)) return null;
    return Math.max(PREVIEW_MIN, Math.round(value));
  } catch {
    return null;
  }
}

function writeStoredPreviewWidth(width) {
  try {
    window.localStorage.setItem(PREVIEW_WIDTH_KEY, String(width));
  } catch {
    /* ignore private mode or quota */
  }
}

function maxPreviewWidth(layout) {
  if (!layout) return 960;
  const gap = parseFloat(window.getComputedStyle(layout).columnGap) || 0;
  const insightsMin = 280;
  return Math.max(PREVIEW_MIN, Math.floor(layout.clientWidth - EDITOR_MIN - insightsMin - gap * 2));
}

function SectionScoreboard({ scores, labels, onReview, busy, canReview }) {
  return (
    <section className="scoreboard" aria-label="Resume scoreboard">
      <div className="scoreboard-head">
        <h2>Section scores</h2>
        <IconButton
          icon={FiZap}
          label="Score sections"
          loading={busy}
          onClick={onReview}
          disabled={!canReview}
        />
      </div>
      <div className="score-row">
        <ScoreRing score={scores?.overall} label="Overall" />
        <ScoreRing score={scores?.ats} label="ATS" />
      </div>
      <ul className="scoreboard-list">
        {SCORE_SECTIONS.map(([key, fallback]) => {
          const score = scores?.sections?.[key];
          const tone = scoreTone(score);
          return (
            <li key={key}>
              <span>{labels?.[key] || fallback}</span>
              <span className="scoreboard-track" aria-hidden>
                <span
                  className={`scoreboard-fill scoreboard-fill-${tone}`}
                  style={{ width: score == null ? '0%' : `${score}%` }}
                />
              </span>
              <strong>{score == null ? '—' : score}</strong>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ScoreRing({ score, label }) {
  if (score == null || score === '') {
    return (
      <div className="score-ring score-ring-low">
        <span className="score-value">—</span>
        <span className="score-label">{label}</span>
      </div>
    );
  }
  const color = score >= 75 ? 'good' : score >= 50 ? 'mid' : 'low';
  return (
    <div className={`score-ring score-ring-${color}`}>
      <span className="score-value">{score}</span>
      <span className="score-label">{label}</span>
    </div>
  );
}

const ResumeStudio = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('chat');
  const [fileName, setFileName] = useState('');
  const [structured, setStructured] = useState(emptyStructuredContent());
  const [contentText, setContentText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [fitResult, setFitResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIdx, setActiveSuggestionIdx] = useState(null);
  const [jobMode, setJobMode] = useState('list');
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get('job_id') || '');
  const [pastedJd, setPastedJd] = useState('');
  const [message, setMessage] = useState(location.state?.parseMessage || '');
  const [aiBusy, setAiBusy] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatMessages, setChatMessages] = useState(() => readChatSession(id)?.messages || [{ role: 'assistant', text: CHAT_GREETING }]);
  const [fieldProposals, setFieldProposals] = useState(() => readChatSession(id)?.proposals || []);
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importSelected, setImportSelected] = useState(() => new Set());
  const autoParsed = useRef(false);
  const chatLogRef = useRef(null);
  const layoutRef = useRef(null);
  const previewPaneRef = useRef(null);
  const dragRef = useRef(null);
  const dragStopRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(readStoredPreviewWidth);
  const previewWidthRef = useRef(previewWidth);
  const [handleLeft, setHandleLeft] = useState(null);
  const [previewBounds, setPreviewBounds] = useState({ current: null, max: 960 });
  const [resizing, setResizing] = useState(false);
  previewWidthRef.current = previewWidth;

  const applyPreviewWidth = useCallback((next, { persist = false } = {}) => {
    const max = maxPreviewWidth(layoutRef.current);
    const width = Math.round(Math.min(max, Math.max(PREVIEW_MIN, next)));
    previewWidthRef.current = width;
    setPreviewWidth(width);
    if (persist) writeStoredPreviewWidth(width);
    return width;
  }, []);

  useEffect(() => () => {
    dragStopRef.current?.();
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const endPreviewResize = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (previewWidthRef.current != null) writeStoredPreviewWidth(previewWidthRef.current);
  };

  const onPreviewResizeDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (window.matchMedia('(max-width: 1180px)').matches) return;
    event.preventDefault();
    const preview = previewPaneRef.current;
    if (!preview) return;
    dragRef.current = {
      startX: event.clientX,
      startWidth: preview.getBoundingClientRect().width,
    };
    setResizing(true);
    event.currentTarget.focus();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const move = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      applyPreviewWidth(drag.startWidth + (drag.startX - ev.clientX));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      dragStopRef.current = null;
      endPreviewResize();
    };
    dragStopRef.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (previewWidthRef.current != null) writeStoredPreviewWidth(previewWidthRef.current);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onPreviewResizeKeyDown = (event) => {
    const step = event.shiftKey ? PREVIEW_NUDGE * 2 : PREVIEW_NUDGE;
    const current = previewPaneRef.current?.getBoundingClientRect().width || previewWidth || PREVIEW_MIN;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      applyPreviewWidth(current + step, { persist: true });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      applyPreviewWidth(current - step, { persist: true });
    } else if (event.key === 'Home') {
      event.preventDefault();
      applyPreviewWidth(PREVIEW_MIN, { persist: true });
    } else if (event.key === 'End') {
      event.preventDefault();
      applyPreviewWidth(maxPreviewWidth(layoutRef.current), { persist: true });
    }
  };
  const importFileRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('job_id')) {
      setJobMode('list');
      setActiveTab('fit');
    }
  }, [searchParams]);

  const { data: resume, isLoading } = useQuery(
    ['resume', id],
    () => api.get(`/resumes/${id}`).then((res) => res.data),
    {
      onSuccess: (data) => {
        setFileName(data.file_name || 'My Resume');
        setStructured(data.structured_content || emptyStructuredContent());
        setContentText(data.content_text || '');
        if (data.ai_insights?.last_analysis) {
          setAnalysis((current) => newerAnalysis(data.ai_insights.last_analysis, current));
        }
        if (data.ai_insights?.last_suggestions) setSuggestions(data.ai_insights.last_suggestions);
      },
    }
  );

  const { data: templates = [] } = useQuery(
    ['resume-templates'],
    () => api.get('/resume-templates').then((res) => res.data),
    { staleTime: 60000 }
  );

  const { data: jobs } = useQuery(
    queryKeys.jobs({ jobViewMode: 'browse', page: 1, limit: 100, role: 'candidate' }),
    () =>
      api
        .get('/jobs/opportunities', { params: { limit: 100, page: 1 } })
        .then((res) => normalizePagedList(res.data, { itemsKey: 'jobs' }).items),
    { staleTime: 60000 }
  );

  const draftPayload = useCallback(
    () => ({
      structured_content: structured,
      content_text: contentText,
    }),
    [structured, contentText]
  );

  const jobContextPayload = useCallback(() => {
    const body = {};
    if (selectedJobId) body.job_id = Number(selectedJobId);
    else if (pastedJd.trim()) body.job_description = pastedJd;
    return body;
  }, [selectedJobId, pastedJd]);

  const fetchSuggestions = useCallback(
    async (extra = {}) => {
      const body = { ...draftPayload(), section: 'all', ...jobContextPayload(), ...extra };
      const res = await api.post(`/resumes/${id}/suggestions`, body);
      const list = res.data.suggestions || [];
      setSuggestions(list);
      if (list.length > 0) {
        setActiveSuggestionIdx(0);
        setActiveTab('fixes');
      }
      return list;
    },
    [draftPayload, jobContextPayload, id]
  );

  const saveMutation = useMutation(
    () =>
      api.put(`/resumes/${id}`, {
        file_name: fileName,
        structured_content: structured,
        content_text: contentText,
        skills: structured.skills,
        summary: structured.summary,
      }),
    {
      onSuccess: () => {
        setDirty(false);
        setMessage('Resume saved.');
        queryClient.invalidateQueries(['resume', id]);
        queryClient.invalidateQueries('my-resumes');
      },
      onError: (err) => setMessage(err.response?.data?.error || 'Save failed'),
    }
  );

  const parseMutation = useMutation(
    () => api.post(`/resumes/${id}/parse`, { content_text: contentText }),
    {
      onSuccess: (res) => {
        setStructured(res.data.structured_content || emptyStructuredContent());
        setContentText(res.data.content_text || '');
        setDirty(false);
        setMessage(res.data.parse_message || 'Form filled from the resume. Review each section, then save.');
        queryClient.invalidateQueries(['resume', id]);
      },
      onError: (err) => setMessage(err.response?.data?.error || 'Parse failed'),
    }
  );

  useEffect(() => {
    autoParsed.current = false;
  }, [id]);

  useEffect(() => {
    if (!resume || autoParsed.current) return;
    if (resumeHasSections(resume.structured_content)) return;
    if (!resume.file_path && !String(resume.content_text || '').trim()) return;
    autoParsed.current = true;
    setMessage('Reading the uploaded resume into each section…');
    parseMutation.mutate();
  }, [resume, parseMutation]);

  useEffect(() => {
    chatLogRef.current?.scrollTo({ top: chatLogRef.current.scrollHeight });
  }, [chatMessages, chatBusy]);

  const skipChatSave = useRef(true);
  useEffect(() => {
    skipChatSave.current = true;
    const stored = readChatSession(id);
    setChatMessages(stored?.messages || [{ role: 'assistant', text: CHAT_GREETING }]);
    setFieldProposals(stored?.proposals || []);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (skipChatSave.current) {
      skipChatSave.current = false;
      return;
    }
    localStorage.setItem(`${CHAT_SESSION_PREFIX}${id}`, JSON.stringify({
      messages: chatMessages.slice(-40),
      proposals: fieldProposals.slice(0, 12),
    }));
  }, [id, chatMessages, fieldProposals]);

  const applyFieldOption = useCallback((proposal, optionIndex) => {
    const option = proposal?.options?.[optionIndex];
    if (!option) return;
    const applied = applyChatUpdates(structured, [{
      op: proposal.op,
      section: proposal.section,
      item_id: proposal.item_id,
      item_index: proposal.item_index,
      field: proposal.field,
      bullet_index: proposal.bullet_index,
      text: option.text || '',
      item: option.item || null,
    }]);
    if (!applied.touched.length) {
      setMessage('That choice did not match a field on the form.');
      return;
    }
    setStructured(applied.structured);
    if (applied.fileName) setFileName(applied.fileName);
    setDirty(true);
    setFieldProposals((current) => current.filter((item) => item.id !== proposal.id));
    const note = `Applied the selected text to ${applied.touched.join(', ')}.`;
    setChatMessages((current) => [...current, { role: 'assistant', text: note }]);
    setMessage(`${note} Review the form, then save.`);
  }, [structured]);

  const dismissFieldOption = useCallback((proposalId) => {
    setFieldProposals((current) => current.filter((item) => item.id !== proposalId));
  }, []);

  const runAnalyze = async () => {
    setAiBusy(true);
    try {
      const res = await api.post(`/resumes/${id}/analyze`, {
        ...draftPayload(),
        ...jobContextPayload(),
        draft_only: false,
      });
      const a = res.data.analysis;
      setAnalysis(a);
      const sugList = res.data.suggestions || [];
      if (sugList.length) {
        setSuggestions(sugList);
        setActiveSuggestionIdx(0);
        setActiveTab('fixes');
      } else {
        setActiveTab('analysis');
      }
      const list = sugList.length ? sugList : await fetchSuggestions();
      setMessage(
        list.length
          ? `Review complete (${a.overall_score}/100). ${list.length} fixes highlighted in editor.`
          : `Review complete (${a.overall_score}/100).`
      );
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Analysis failed');
    } finally {
      setAiBusy(false);
    }
  };

  const runScore = async () => {
    if (jobMode === 'list' && !selectedJobId) {
      setMessage('Select a job or paste a job description first.');
      return;
    }
    if (jobMode === 'paste' && !pastedJd.trim()) {
      setMessage('Paste a job description first.');
      return;
    }
    setAiBusy(true);
    try {
      const body = { ...draftPayload(), ...jobContextPayload() };
      const res = await api.post(`/resumes/${id}/score`, body);
      setFitResult(res.data);
      const sections = res.data.section_scores;
      if (sections && Object.keys(sections).length) {
        setAnalysis((current) => ({
          ...(current || {}),
          section_scores: sections,
          analyzed_at: res.data.scored_at || new Date().toISOString(),
        }));
      }
      setActiveTab('fit');
      const list = Array.isArray(res.data.suggestions)
        ? res.data.suggestions
        : await fetchSuggestions(jobContextPayload());
      setSuggestions(list);
      if (list.length) {
        setActiveSuggestionIdx(0);
        setActiveTab('fixes');
      }
      setMessage(
        list.length
          ? `Match score ${res.data.score}/100. ${list.length} tailoring fixes ready.`
          : `Match score ${res.data.score}/100.`
      );
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Scoring failed');
    } finally {
      setAiBusy(false);
    }
  };

  const runSuggestionsOnly = async () => {
    setAiBusy(true);
    try {
      const list = await fetchSuggestions();
      setMessage(
        list.length
          ? `${list.length} suggestions — fields highlighted in editor. Click to focus.`
          : 'No suggestions returned. Add more resume content or check AI settings.'
      );
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Suggestions failed');
    } finally {
      setAiBusy(false);
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const history = chatMessages
      .filter((item) => item.role === 'user' || item.role === 'assistant')
      .slice(-16)
      .map((item) => ({ role: item.role, text: item.text }));
    setChatMessages((current) => [...current, { role: 'user', text }]);
    setChatInput('');
    setChatBusy(true);
    try {
      const res = await api.post(`/resumes/${id}/chat`, {
        message: text,
        history,
        ...draftPayload(),
        ...jobContextPayload(),
      });
      const proposals = res.data.proposals || [];
      const reply = proposals.length
        ? `${res.data.reply || 'Choices are ready.'} Pick one on the form.`
        : (res.data.reply || 'No change was suggested.');
      setFieldProposals(proposals);
      if (proposals.length) setActiveTab('chat');
      setChatMessages((current) => [...current, { role: 'assistant', text: reply }]);
    } catch (err) {
      setChatMessages((current) => [
        ...current,
        { role: 'assistant', text: err.response?.data?.error || err.message || 'The assistant could not update the resume.' },
      ]);
    } finally {
      setChatBusy(false);
    }
  };

  const suggestionHighlightKeys = useMemo(() => {
    const keys = new Set();
    for (const s of suggestions) {
      keys.add(suggestionToHighlightKey(s, structured));
    }
    return keys;
  }, [suggestions, structured]);

  const suggestionKey = useMemo(() => {
    if (activeSuggestionIdx == null || !suggestions[activeSuggestionIdx]) return null;
    return suggestionToHighlightKey(suggestions[activeSuggestionIdx], structured);
  }, [activeSuggestionIdx, suggestions, structured]);
  const activeSuggestionKey = focusKey || suggestionKey;

  const handleApplySuggestion = (idx) => {
    const s = suggestions[idx];
    if (!s?.proposed_text) {
      setActiveSuggestionIdx(idx);
      setMessage('No auto-apply text — use the suggestion as guidance and edit the highlighted field.');
      return;
    }
    const next = applySuggestionToStructured(structured, s);
    setStructured(next);
    setDirty(true);
    setFocusKey(null);
    setActiveSuggestionIdx(idx);
    setMessage('Fix applied — review and save.');
  };

  const handleStructuredChange = (next) => {
    setStructured(next);
    setDirty(true);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImportBusy(true);
    setMessage('');
    try {
      let contentTextFromFile = '';
      try {
        contentTextFromFile = await extractTextFromFile(file);
      } catch {
        contentTextFromFile = '';
      }
      const body = new FormData();
      body.append('resume', file);
      if (contentTextFromFile) body.append('content_text', contentTextFromFile);
      const res = await api.post(`/resumes/${id}/import-preview`, body);
      const sections = res.data.sections || [];
      if (!sections.length) {
        setMessage(res.data.message || 'No sections to import.');
        return;
      }
      setImportPreview({
        structured: res.data.structured_content,
        sections,
      });
      setImportSelected(new Set(sections.map(importChoiceKey)));
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not read that resume.');
    } finally {
      setImportBusy(false);
    }
  };

  const toggleImportChoice = (choice) => {
    setImportSelected((current) => {
      const next = new Set(current);
      if (next.has(choice)) next.delete(choice);
      else next.add(choice);
      return next;
    });
  };

  const applyImportSelection = () => {
    if (!importPreview) return;
    const selection = selectionFromImport(importPreview.sections, importSelected);
    const next = mergeImportedResume(structured, importPreview.structured, selection, (prefix) => {
      if (prefix === 'edu') return newEducationId();
      if (prefix === 'cert') return newCertificationId();
      if (prefix === 'lang') return newLanguageId();
      if (prefix === 'proj') return newProjectId();
      if (prefix === 'sec') return newCustomSectionId();
      return newExperienceId();
    });
    setStructured(next);
    setDirty(true);
    setImportPreview(null);
    setMessage('Selected sections added. Review and save.');
  };

  const hasEditorContent = useMemo(() => resumeHasSections(structured), [structured]);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const preview = previewPaneRef.current;
    if (!layout || !preview) return undefined;
    const place = () => {
      const editor = layout.querySelector('.studio-editor-pane');
      if (!editor) return;
      const layoutRect = layout.getBoundingClientRect();
      const editorRect = editor.getBoundingClientRect();
      const previewRect = preview.getBoundingClientRect();
      const left = Math.round((editorRect.right + previewRect.left) / 2 - layoutRect.left);
      const current = Math.round(previewRect.width);
      const max = maxPreviewWidth(layout);
      setHandleLeft((prev) => (prev === left ? prev : left));
      setPreviewBounds((prev) => (
        prev.current === current && prev.max === max ? prev : { current, max }
      ));
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(layout);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [isLoading, resume]);

  if (isLoading) {
    return <div className="loading">Loading Resume Builder…</div>;
  }

  if (!resume) {
    return <div className="error">Resume not found</div>;
  }

  const recommendationLabel = {
    apply: 'Strong fit — consider applying',
    tailor_first: 'Promising — tailor resume first',
    skip: 'Low fit — focus elsewhere',
  };
  const appliedPreviewWidth = previewWidth == null
    ? null
    : Math.max(PREVIEW_MIN, Math.min(previewWidth, previewBounds.max));

  return (
    <div className="resume-studio">
      <header className="studio-header">
        <div className="studio-header-left">
          <IconButton
            icon={FiArrowLeft}
            label="Back to AI Studio"
            variant="ghost"
            onClick={() => navigate('/ai-studio')}
          />
          <h1>Resume Builder</h1>
        </div>
        <div className="studio-header-actions">
          <input
            ref={importFileRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            hidden
            onChange={handleImportFile}
          />
          <IconButton
            icon={FiUpload}
            label="Import sections from a resume"
            loading={importBusy}
            onClick={() => importFileRef.current?.click()}
          />
          {(resume.file_path || contentText) && (
            <IconButton
              icon={FiRefreshCw}
              label="Fill form from resume"
              loading={parseMutation.isLoading}
              onClick={() => parseMutation.mutate()}
            />
          )}
          <IconButton
            icon={FiSave}
            label={dirty ? 'Save changes' : 'Saved'}
            variant="primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
            disabled={!dirty && !saveMutation.isLoading}
          />
        </div>
      </header>

      {!hasEditorContent && (
        <div className="studio-message studio-message-warn">
          Add experience, skills, or another section before running AI. Contact details alone are not sent.
        </div>
      )}

      {message && (
        <div className="studio-message" role="status">
          {message}
          <button type="button" className="studio-message-dismiss" onClick={() => setMessage('')}>
            ×
          </button>
        </div>
      )}

      <div
        className={`studio-layout${appliedPreviewWidth != null ? ' studio-layout-sized' : ''}`}
        ref={layoutRef}
        style={appliedPreviewWidth != null ? { '--studio-preview-width': `${appliedPreviewWidth}px` } : undefined}
      >
        <div className="studio-editor-pane">
          <div className="studio-pane-title">
            <FiEdit3 /> Resume sections
            {suggestions.length > 0 && (
              <span className="badge badge-warning">{suggestions.length} fixes to review</span>
            )}
          </div>
          <ResumeEditor
            structured={structured}
            onChange={handleStructuredChange}
            activeSuggestionKeys={suggestionHighlightKeys}
            activeSuggestionKey={activeSuggestionKey}
            sectionScores={analysis?.section_scores}
            fileName={fileName}
            onFileNameChange={(v) => {
              setFileName(v);
              setDirty(true);
            }}
            fieldProposals={fieldProposals}
            onPickFieldOption={applyFieldOption}
            onDismissFieldOption={dismissFieldOption}
          />
        </div>

        {handleLeft != null && (
          <div
            className={`studio-resize-handle${resizing ? ' is-dragging' : ''}`}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize preview"
            aria-valuemin={PREVIEW_MIN}
            aria-valuemax={previewBounds.max}
            aria-valuenow={previewBounds.current ?? previewWidth ?? PREVIEW_MIN}
            tabIndex={0}
            style={{ left: `${handleLeft}px` }}
            onPointerDown={onPreviewResizeDown}
            onKeyDown={onPreviewResizeKeyDown}
          />
        )}

        <aside className="studio-preview-pane" aria-label="Resume preview" ref={previewPaneRef}>
          <ResumePreview structured={structured} fileName={fileName} templates={templates} resumeId={id} />
        </aside>

        <aside className="studio-insights-pane">
          <div className="studio-insights-pin">
          <SectionScoreboard
            scores={{
              overall: analysis?.overall_score,
              ats: analysis?.ats_score,
              sections: analysis?.section_scores,
            }}
            labels={structured.section_labels}
            onReview={runAnalyze}
            busy={aiBusy}
            canReview={hasEditorContent}
          />
          </div>

          <div className="studio-insights-scroll">
          <div className="studio-tabs">
            {[
              { id: 'chat', label: 'Chat', icon: FiMessageSquare },
              { id: 'fit', label: 'Job fit', icon: FiTarget },
              { id: 'analysis', label: 'Review', icon: FiZap },
              { id: 'fixes', label: 'Fixes', icon: FiEdit3 },
            ].map(({ id: tabId, label, icon: Icon }) => (
              <button
                key={tabId}
                type="button"
                className={`studio-tab ${activeTab === tabId ? 'active' : ''}`}
                aria-label={label}
                title={label}
                onClick={() => setActiveTab(tabId)}
              >
                <Icon aria-hidden />
                {tabId === 'fixes' && suggestions.length > 0 && (
                  <span className="studio-tab-count">{suggestions.length}</span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'chat' && (
            <div className="studio-panel studio-chat">
              <div className="studio-chat-log" ref={chatLogRef}>
                {chatMessages.map((item, index) => (
                  <div key={`${item.role}-${index}`} className={`studio-chat-bubble studio-chat-bubble--${item.role}`}>
                    {item.text}
                  </div>
                ))}
                {chatBusy && <div className="studio-chat-bubble studio-chat-bubble--assistant">Preparing choices from the resume…</div>}
              </div>
              <form
                className="studio-chat-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendChat();
                }}
              >
                <textarea
                  id="resume-chat-input"
                  aria-label="Message"
                  rows={3}
                  value={chatInput}
                  placeholder="Make the summary shorter and more specific…"
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  disabled={chatBusy}
                />
                <IconButton
                  type="submit"
                  icon={FiSend}
                  label="Send"
                  variant="primary"
                  loading={chatBusy}
                  disabled={!chatInput.trim()}
                />
              </form>
            </div>
          )}

          {activeTab === 'fit' && (
            <div className="studio-panel">
              <div className="job-mode-toggle">
                <IconButton
                  icon={FiBriefcase}
                  label="Score a saved job"
                  active={jobMode === 'list'}
                  onClick={() => setJobMode('list')}
                />
                <IconButton
                  icon={FiClipboard}
                  label="Paste a job description"
                  active={jobMode === 'paste'}
                  onClick={() => setJobMode('paste')}
                />
              </div>
              {jobMode === 'list' ? (
                <select value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)}>
                  <option value="">Select a job…</option>
                  {(jobs || []).map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title} — {job.company}
                    </option>
                  ))}
                </select>
              ) : (
                <textarea
                  rows={8}
                  placeholder="Paste the full job description here…"
                  value={pastedJd}
                  onChange={(e) => setPastedJd(e.target.value)}
                />
              )}
              <div className="studio-actions">
                <IconButton
                  icon={FiTarget}
                  label="Score and get fixes"
                  variant="primary"
                  loading={aiBusy}
                  onClick={runScore}
                  disabled={!hasEditorContent}
                />
              </div>

              {fitResult && (
                <div className="fit-results">
                  <ScoreRing score={fitResult.score} label="Match score" />
                  <p className={`fit-recommendation fit-${fitResult.recommendation}`}>
                    {recommendationLabel[fitResult.recommendation] || fitResult.summary}
                  </p>
                  {fitResult.summary && <p className="fit-summary">{fitResult.summary}</p>}
                  {fitResult.matched_keywords?.length > 0 && (
                    <div className="keyword-block">
                      <h4>Matched keywords</h4>
                      <div className="keyword-tags">
                        {fitResult.matched_keywords.map((k) => (
                          <span key={k} className="keyword-tag keyword-good">{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {fitResult.missing_keywords?.length > 0 && (
                    <div className="keyword-block">
                      <h4>Gaps to address</h4>
                      <div className="keyword-tags">
                        {fitResult.missing_keywords.map((k) => (
                          <span key={k} className="keyword-tag keyword-missing">{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {fitResult.tailoring_tips?.length > 0 && (
                    <ul className="tips-list">
                      {fitResult.tailoring_tips.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  )}
                  {selectedJobId && (
                    <Link to={`/jobs/${selectedJobId}`} className="btn btn-icon btn-secondary" aria-label="View job" title="View job">
                      <FiExternalLink aria-hidden />
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'analysis' && (
            <div className="studio-panel">
              <div className="studio-actions">
                <IconButton
                  icon={FiZap}
                  label="Review resume"
                  variant="primary"
                  loading={aiBusy}
                  onClick={runAnalyze}
                  disabled={!hasEditorContent}
                />
              </div>
              {analysis && (
                <div className="analysis-results">
                  <div className="score-row">
                    <ScoreRing score={analysis.overall_score} label="Overall" />
                    <ScoreRing score={analysis.ats_score} label="ATS" />
                  </div>
                  {analysis.summary && <p className="fit-summary">{analysis.summary}</p>}
                  {analysis.strengths?.length > 0 && (
                    <div className="insight-block insight-good">
                      <h4><FiCheckCircle /> Strengths</h4>
                      <ul>{analysis.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {analysis.gaps?.length > 0 && (
                    <div className="insight-block insight-warn">
                      <h4><FiAlertCircle /> Gaps</h4>
                      <ul>{analysis.gaps.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {analysis.ats_tips?.length > 0 && (
                    <div className="insight-block">
                      <h4>ATS tips</h4>
                      <ul>{analysis.ats_tips.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'fixes' && (
            <div className="studio-panel">
              <div className="studio-actions">
                <IconButton
                  icon={FiRefreshCw}
                  label="Refresh fixes"
                  variant="primary"
                  loading={aiBusy}
                  onClick={runSuggestionsOnly}
                  disabled={!hasEditorContent}
                />
              </div>
              <ul className="suggestions-list">
                {suggestions.map((s, idx) => (
                  <li
                    key={s.id || idx}
                    className={`suggestion-item severity-${s.severity} ${activeSuggestionIdx === idx ? 'active' : ''}`}
                  >
                    <button
                      type="button"
                      className="suggestion-focus-btn"
                      onClick={() => {
                        setFocusKey(null);
                        setActiveSuggestionIdx(idx);
                      }}
                    >
                      <span className="suggestion-section">{s.section}</span>
                      <strong>{s.issue}</strong>
                      <p>{s.suggestion}</p>
                    </button>
                    {s.proposed_text && (
                      <IconButton
                        icon={FiCheck}
                        label="Apply fix"
                        size="sm"
                        onClick={() => handleApplySuggestion(idx)}
                      />
                    )}
                  </li>
                ))}
              </ul>
              {suggestions.length === 0 && (
                <p className="panel-intro">Run Analyze or Score to generate contextual fixes.</p>
              )}
            </div>
          )}
          </div>
        </aside>
      </div>

      <Modal
        open={Boolean(importPreview)}
        onClose={() => setImportPreview(null)}
        ariaLabel="Add sections"
        contentClassName="import-sections-modal"
      >
        <div className="modal-header">
          <h2>Add sections</h2>
          <div className="studio-header-actions">
            <IconButton icon={FiX} label="Cancel" variant="ghost" onClick={() => setImportPreview(null)} />
            <IconButton
              icon={FiCheck}
              label="Add selected sections"
              variant="primary"
              onClick={applyImportSelection}
              disabled={importSelected.size === 0}
            />
          </div>
        </div>
        <ul className="import-section-list">
          {(importPreview?.sections || []).map((section) => {
            const choice = importChoiceKey(section);
            return (
              <li key={choice}>
                <label className="import-section-option">
                  <input
                    type="checkbox"
                    checked={importSelected.has(choice)}
                    onChange={() => toggleImportChoice(choice)}
                  />
                  <span>
                    <span className="import-section-kind">{section.group}</span>
                    {section.label && section.label !== section.group ? (
                      <span className="import-section-label">{section.label}</span>
                    ) : null}
                  </span>
                  {section.preview ? <span className="import-section-preview">{section.preview}</span> : null}
                </label>
              </li>
            );
          })}
        </ul>
      </Modal>
    </div>
  );
};

export default ResumeStudio;
